import React, { useMemo, useState } from 'react';
import { MessageSquare, CheckCheck, Copy, Check } from 'lucide-react';
import { clsx } from 'clsx';
import { useSettingsStore } from '@/stores';
import { generateTemplatePreview } from '@/utils/templateEngine';
import { formatWhatsAppMessage } from '@/utils/whatsappFormatter';
import { useToast } from '@/components/common/Toast';

export interface WhatsAppMessagePreviewProps {
  /** Texto del mensaje ya interpolado (para data real) */
  message?: string | null;
  /** Plantilla en crudo con tags (para datos de ejemplo/mock) */
  template?: string;
  /** Tipo de plantilla a interpolar si se usa `template` */
  previewType?: 'appointment' | 'crm' | 'campaign';
  /** Variante visual:
   * - 'simulator': Contenedor con marco de simulación de chat WhatsApp, fondo oficial y pie informativo.
   * - 'bubble': Solo la burbuja verde/oscura compacta de chat WhatsApp.
   */
  variant?: 'simulator' | 'bubble';
  /** Hora a mostrar en el mensaje (ej: '17:30', '15:00', o hora de la cita). */
  timestamp?: string;
  /** Mostrar doble check azul de WhatsApp (por defecto true) */
  showDoubleCheck?: boolean;
  /** Mostrar botón de copiar mensaje dentro de la burbuja */
  showCopyButton?: boolean;
  /** Mostrar contador de caracteres en el pie (solo en simulator o si se activa) */
  showCharCount?: boolean;
  /** Nota al pie en modo simulator (ej: 'Variables sustituidas con datos de ejemplo') */
  footerNote?: string;
  /** Texto cuando el mensaje está vacío */
  emptyText?: string;
  /** Clases CSS adicionales para el contenedor principal */
  className?: string;
  /** Clases CSS adicionales para la burbuja interna */
  bubbleClassName?: string;
}

export function WhatsAppMessagePreview({
  message,
  template,
  previewType = 'appointment',
  variant = 'bubble',
  timestamp,
  showDoubleCheck = true,
  showCopyButton = false,
  showCharCount = false,
  footerNote,
  emptyText = 'Mensaje vacío',
  className,
  bubbleClassName,
}: WhatsAppMessagePreviewProps) {
  const [copied, setCopied] = useState(false);
  const { showToast } = useToast();
  const settings = useSettingsStore();

  const resolvedText = useMemo(() => {
    if (message !== undefined && message !== null) {
      return message;
    }
    if (template !== undefined) {
      return generateTemplatePreview(template || '', previewType, settings);
    }
    return '';
  }, [message, template, previewType, settings]);

  const charCount = resolvedText.length;
  const displayTime = timestamp ?? (variant === 'simulator' ? '17:30' : undefined);

  const formattedContent = useMemo(() => {
    if (!resolvedText) return null;
    return formatWhatsAppMessage(resolvedText);
  }, [resolvedText]);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!resolvedText) return;
    try {
      await navigator.clipboard.writeText(resolvedText);
      setCopied(true);
      showToast('Mensaje copiado al portapapeles', 'success');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showToast('No se pudo copiar al portapapeles', 'error');
    }
  };

  const bubbleContent = (
    <div
      className={clsx(
        'relative max-w-md bg-[#d9fdd3] dark:bg-[#005c4b] text-[#111b21] dark:text-[#e9edef] rounded-2xl rounded-tr-xs p-3 shadow-[0_1px_1px_rgba(11,20,26,0.12)] dark:shadow-[0_1px_1px_rgba(0,0,0,0.35)] text-[13px] font-sans leading-[19px] group transition-all',
        bubbleClassName
      )}
    >
      {/* Botón flotante de copiado opcional */}
      {showCopyButton && resolvedText && (
        <button
          type="button"
          onClick={handleCopy}
          className="absolute top-2 right-2 p-1 rounded-md bg-black/5 dark:bg-white/10 hover:bg-black/10 dark:hover:bg-white/20 text-[#667781] dark:text-[#8696a0] hover:text-[#111b21] dark:hover:text-white transition-colors cursor-pointer opacity-80 group-hover:opacity-100"
          title="Copiar mensaje"
        >
          {copied ? <Check size={12} className="text-emerald-600 dark:text-emerald-400" /> : <Copy size={12} />}
        </button>
      )}

      {/* Contenido del mensaje formateado */}
      <div className="select-text whitespace-pre-wrap break-words">
        {formattedContent || <span className="italic opacity-60 text-xs">{emptyText}</span>}
      </div>

      {/* Timestamp y doble check azul */}
      {(displayTime || showDoubleCheck) && (
        <div className="flex items-center justify-end gap-1 mt-1 text-[11px] text-[#667781] dark:text-[#8696a0] select-none leading-none">
          {displayTime && <span className="font-normal">{displayTime}</span>}
          {showDoubleCheck && <CheckCheck size={14} className="text-[#53bdeb] stroke-[2.2]" />}
        </div>
      )}
    </div>
  );

  if (variant === 'simulator') {
    return (
      <div className={clsx('space-y-2', className)}>
        {/* Contenedor de simulación con fondo de wallpaper WhatsApp */}
        <div className="bg-[#efeae2]/85 dark:bg-[#0b141a]/80 p-3 sm:p-4 rounded-xl border border-black/8 dark:border-white/8 shadow-2xs">
          <div className="flex items-center gap-1.5 mb-2.5 text-[10px] font-semibold uppercase tracking-wider text-text-muted">
            <MessageSquare size={12} className="text-emerald-600 dark:text-emerald-400" />
            <span>Simulación en WhatsApp</span>
          </div>

          {bubbleContent}
        </div>

        {(footerNote || showCharCount) && (
          <div className="flex items-center justify-between text-[11px] text-text-muted px-1">
            <span>{footerNote || ''}</span>
            {showCharCount && <span>{charCount} caracteres</span>}
          </div>
        )}
      </div>
    );
  }

  return <div className={className}>{bubbleContent}</div>;
}
